/* eslint-disable @typescript-eslint/no-explicit-any */
import { Formik, FormikHelpers } from 'formik'
import { omit } from 'lodash'
// import { FC, useEffect, useState, useMemo, useCallback } from 'react'
import { FC, useEffect, useState, useCallback } from 'react'
import * as Yup from 'yup'

import FormComponent from 'components/forms/elements/Form'
import FormButton from 'components/forms/elements/FormButton'
import FormButtonContainer from 'components/forms/elements/FormButtonContainer'
import FormGrid from 'components/forms/elements/FormGrid'
import Loading from 'components/Loading'
import { EVENT_SHIRT_DETAILS, SHIRT_KEY } from 'constants/models'
import { Category, Event, Participant } from 'models/API'
import { generateInitFormParticipant, hasShirtType } from 'utils/ApiHelpers'

import content from './content'
import schema from './schema'

interface Props {
  participant: Participant
  event: Event
  onSubmit: (values: Yup.InferType<typeof schema>) => Promise<void>
  onCancel: () => void
}

type FormValues = Yup.InferType<typeof schema>

const Form: FC<Props> = ({ participant, event, onSubmit, onCancel }) => {
  const [formInitialValues, setFormInitialValues] = useState<FormValues>()
  const [selectedCategory, setSelectedCategory] = useState<Category>()
  const [categories, setCategories] = useState<Category[]>()

  const [tandemInfo, setTandemInfo] = useState<any>({
    isTandem: false,
    tandemAgeRange: '',
    eventEndDateTime: event.endDateTime,
  })

  const [fullShirtDetails, setFullShirtDetails] = useState<any>({
    hasShirt2: false,
    shirtDetails: event.shirtDetails ?? EVENT_SHIRT_DETAILS,
  })

  useEffect(() => {
    const categories = (event?.categories?.items ?? []) as unknown as Category[]
    setCategories(categories)
    if (participant) {
      const category = categories?.filter(
        (c) => c.id === participant.categoryId,
      )?.[0]

      if (category) {
        setFormInitialValues(
          generateInitFormParticipant(
            participant,
            event,
            category,
          ) as FormValues,
        )
        setSelectedCategory(category)
        setFullShirtDetails((prevState) => ({
          ...prevState,
          hasShirt2: category.hasShirt2 || false,
        }))
        const tandemInfo = {
          isTandem: !!category.isTandem,
          tandemAgeRange: category.tandemAgeRange ?? '',
          eventEndDateTime: event.endDateTime,
        }
        setTandemInfo(tandemInfo)
      }
    }
  }, [participant, event])

  const handleSubmit = useCallback(
    async (values: FormValues, actions: FormikHelpers<FormValues>) => {
      Object.keys(values).forEach((key) => {
        if (typeof values[key] === 'string') values[key] = values[key]?.trim()
      })
      const newValues = {
        ...omit(values, [
          'shirtSize2',
          'shirtType2',
          'hasBibName',
          'tandemName',
          'tandemBirthdate',
          'tandemShirtSize',
          'isTandem',
          'tandemAgeRange',
          'eventEndDateTime',
          'hasJersey',
        ]),
        ...(values.hasShirt2 && {
          ...('shirtSize2' in values && {
            shirtSize2: values.shirtSize2,
          }),
          ...('shirtType2' in values && {
            shirtType2: values.shirtType2,
          }),
        }),
        ...(values.isTandem && {
          tandemName: values.tandemName,
          tandemBirthdate: values.tandemBirthdate,
          tandemShirtSize: values.tandemShirtSize,
        }),
        ...(values.hasJersey && {
          jerseySize: values.jerseySize,
        }),
      }

      await onSubmit(newValues)
      actions.setSubmitting(false)
    },
    [tandemInfo],
  )

  // const getTotalParticipants = useMemo(
  //   () =>
  //     event.categories.items.reduce(
  //       (acc, cur) => acc + cur?.categoryCount?.count,
  //       0,
  //     ),
  //   [event],
  // )

  if (!selectedCategory || categories?.length === 0)
    return <Loading loading={true} />

  return (
    <Formik
      initialValues={formInitialValues}
      validateOnMount
      enableReinitialize
      validateOnBlur
      validationSchema={schema}
      onSubmit={handleSubmit}>
      {(form) => {
        const updateField = (key, value) => {
          form.setFieldValue(key, value)
          form.setTouched({
            ...form.touched,
            [key]: participant[key] !== value,
          })
        }

        const handleCategoryChange = (e) => {
          const selectedCat =
            categories.filter((cat) => cat.id === e.target.value)?.[0] || null
          if (selectedCat) {
            setFullShirtDetails((prevState) => ({
              ...prevState,
              hasShirt2: selectedCat.hasShirt2 ?? false,
            }))
            setSelectedCategory(selectedCat)
            updateField('categoryId', selectedCat.id)
            updateField('hasShirt2', selectedCat.hasShirt2 ?? false)
            if (selectedCat.hasShirt2) {
              updateField(
                'shirtSize2',
                form.values.shirtSize2 ?? participant.shirtSize2 ?? '',
              )
              if (hasShirtType(fullShirtDetails, SHIRT_KEY.SHIRT2)) {
                updateField(
                  'shirtType2',
                  form.values.shirtType2 ?? participant.shirtType2 ?? '',
                )
              }
            }
            form.setFieldValue('relayEvent', form.values.relayEvent ?? [])
            form.setFieldValue('teamType', selectedCat.teamType ?? '')
            form.setFieldValue('isTriathlon', !!selectedCat.isIndividual)
            form.setFieldValue('isIndividual', !!selectedCat.isTriathlon)

            // if ('isTandem' in selectedCat && selectedCat.isTandem) {
            setTandemInfo({
              isTandem: selectedCat.isTandem ?? false,
              tandemAgeRange: `${selectedCat.tandemAgeRange}`,
              eventEndDateTime: event.endDateTime,
            })
            form.setFieldValue('isTandem', selectedCat.isTandem || false)
            form.setFieldValue(
              'tandemAgeRange',
              selectedCat.tandemAgeRange ?? '',
            )
            form.setFieldValue('eventEndDateTime', event.endDateTime)
            // }
          }
        }

        const handleRelayEventChange = (e) => {
          if (e.target.name.startsWith('relayEvent')) {
            const nameIdx = e.target.name.indexOf('-')
            if (nameIdx !== -1) {
              const relayEventValue = e.target.name.substr(nameIdx + 1)

              let newRelayEvent = (
                'relayEvent' in form.values ? form.values.relayEvent : []
              ) as string[]
              if (e.target.checked) {
                if (!newRelayEvent.includes(relayEventValue))
                  newRelayEvent = [...newRelayEvent, relayEventValue]
              } else {
                newRelayEvent = newRelayEvent.filter(
                  (name) => name !== relayEventValue,
                )
              }

              updateField('relayEvent', newRelayEvent)
            }
          }
        }

        // console.log({
        //   // for category form
        //   values: form.values,
        //   categories,
        //   onCategoryChange: handleCategoryChange,
        //   currentRelayEvent: [],
        //   selectedRelayEvent: [],
        //   onRelayEventChange: handleRelayEventChange,
        //   fullShirtDetails,
        //   hasJersey: selectedCategory.hasJersey ?? false,
        //   hasBibName: event.hasBibName ?? false,
        //   subCategories: event.subCategories ?? null,

        //   tandemInfo,

        //   // common
        //   formDisabled: form.isSubmitting,
        // })
        // console.log({ form })
        return (
          <FormComponent>
            <FormGrid
              content={content({
                // for category form
                values: form.values,
                categories,
                onCategoryChange: handleCategoryChange,
                currentRelayEvent: [],
                selectedRelayEvent: [],
                onRelayEventChange: handleRelayEventChange,
                fullShirtDetails,
                hasJersey: selectedCategory.hasJersey ?? false,
                hasBibName: event.hasBibName ?? false,
                subCategories: event.subCategories ?? null,

                // tandem form
                tandemInfo,

                // common
                formDisabled: form.isSubmitting,
                updateField,
              })}
            />
            <FormButtonContainer wrap={false} my={2}>
              <FormButton
                secondary
                onClick={onCancel}
                disabled={form.isSubmitting}>
                Cancel
              </FormButton>

              <FormButton
                submit
                primary
                outlined
                disabled={!form.isValid || !form.dirty || form.isSubmitting}
                loading={form.isSubmitting}>
                Update
              </FormButton>
            </FormButtonContainer>
          </FormComponent>
        )
      }}
    </Formik>
  )
}

export default Form
